import React from "react";
import { Link } from "react-router-dom";
import { Cpu, Server, HardDrive, Shield, Workflow, MemoryStick, ArrowRight } from "lucide-react";

export default function About() {
  return (
    <div data-testid="about-page" className="container-pad py-12 md:py-20">
      <div className="overline">// ABOUT · BUSINESS MODEL</div>
      <h1 className="mt-4 font-display text-4xl md:text-6xl tracking-tight max-w-4xl">
        We rent and manage <span className="text-signal">cloud servers.</span>
      </h1>
      <p className="mt-5 max-w-3xl text-zinc-300 text-lg leading-relaxed">
        CloudRent is a <strong className="text-white">cloud hosting SaaS</strong> where
        individuals and businesses rent virtual private servers (VPS) and dedicated
        bare-metal machines, then manage them through our web dashboard.
      </p>

      <section className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-4">
          <div className="overline">// WHAT WE SELL</div>
          <h2 className="mt-2 font-display text-3xl tracking-tight">A single product line</h2>
          <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
            CloudRent sells one thing: cloud server hosting. Customers receive SSH access
            and a control panel after payment. 100% software-only — no physical goods are
            shipped, no hardware ever leaves our datacenters.
          </p>
        </div>
        <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="card-flat p-6">
            <Server size={18} className="text-signal" />
            <div className="mt-3 font-display text-lg">Virtual Private Servers</div>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
              KVM virtual machines with dedicated CPU, RAM, and NVMe storage. General-purpose,
              storage, memory, and CPU-optimized line-ups. Resize and snapshot anytime.
            </p>
            <div className="mt-3 badge-mono">SAAS · HOSTING</div>
          </div>
          <div className="card-flat p-6">
            <Cpu size={18} className="text-signal" />
            <div className="mt-3 font-display text-lg">Dedicated Bare-Metal</div>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
              Single-tenant physical servers with full IPMI access, ECC memory, and a
              4-hour hardware-replacement SLA. Customers retain full root access.
            </p>
            <div className="mt-3 badge-mono">SAAS · HOSTING</div>
          </div>
        </div>
      </section>

      <section className="mt-16 card-flat p-8 md:p-10">
        <div className="overline">// BUSINESS MODEL — PLAIN ENGLISH</div>
        <h2 className="mt-2 font-display text-3xl tracking-tight">
          How CloudRent works
        </h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-zinc-300 leading-relaxed">
          <div>
            <div className="font-mono text-signal text-xs tracking-widest">01 · ORDER</div>
            <p className="mt-2">
              Customers create an account and select a VPS, dedicated server, or API plan
              from our catalog. Payment is taken in USD via our designated checkout
              provider.
            </p>
          </div>
          <div>
            <div className="font-mono text-signal text-xs tracking-widest">02 · PROVISION</div>
            <p className="mt-2">
              When the payment is verified, our system provisions the requested server in
              the chosen region and surfaces the SSH credentials in the customer's dashboard.
            </p>
          </div>
          <div>
            <div className="font-mono text-signal text-xs tracking-widest">03 · MANAGE</div>
            <p className="mt-2">
              Customers can resize, reboot, reinstall, snapshot, or cancel servers from the
              dashboard at any time. Plans renew monthly until cancelled.
            </p>
          </div>
        </div>
      </section>

      <section className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { Icon: Shield, t: "SECURE CHECKOUT", d: "Card data is entered on our checkout provider's certified environment — never on our servers." },
          { Icon: Workflow, t: "EMAIL RECEIPTS", d: "Every payment triggers an emailed receipt that includes your server credentials." },
          { Icon: HardDrive, t: "DAILY BACKUPS", d: "Snapshots and backups run automatically on all VPS plans and are retained for 7 days." },
          { Icon: MemoryStick, t: "SOFTWARE-ONLY", d: "All deliverables are server access credentials. No physical goods are shipped." },
        ].map(({ Icon, t, d }) => (
          <div key={t} className="card-flat p-5">
            <Icon size={16} className="text-signal" />
            <div className="mt-3 font-mono text-[11px] uppercase tracking-widest text-white">{t}</div>
            <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{d}</p>
          </div>
        ))}
      </section>

      <section className="mt-16 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between border-t border-zinc-800 pt-10">
        <div>
          <div className="overline">// QUESTIONS?</div>
          <p className="mt-2 text-zinc-300">
            Email <a href="mailto:sales@cloudrentvps.com" className="text-signal hover:underline">sales@cloudrentvps.com</a> —
            we reply within one business day.
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/pricing" className="btn-signal h-11 px-5 text-sm font-mono tracking-widest">
            SEE PRICING <ArrowRight size={14} />
          </Link>
          <Link to="/contact" className="btn-ghost h-11 px-5 text-sm font-mono tracking-widest">
            CONTACT US
          </Link>
        </div>
      </section>
    </div>
  );
}
